// @flow
import moment from 'moment'
import { compose, withProps, withPropsOnChange, withState } from 'recompose'
import { statesByCode } from '../../../common/helpers'

// This is a list of available helper filters that can be used to not have to rewrite any logic
export const filters = {
  date: (date, filterData) => {
    if (filterData.date && filterData.date.startDate) {
      if (moment(date).isBefore(filterData.date.startDate)) {
        return false
      }
    }

    if (filterData.date && filterData.date.endDate) {
      if (moment(date).isAfter(filterData.date.endDate)) {
        return false
      }
    }
    return true
  },
  location: (item, filterData) => {
    if (filterData.locale) {
      // Check country
      if (item.country !== filterData.locale.country) {
        return false
      }

      // Check state
      if (item.state !== filterData.locale.state) {
        return false
      }
    }
    return true
  },
  locationSearch: (item, filterData) => {
    if (filterData.search) {
      let searchText = filterData.search.trim().toLowerCase()
      // Name, city, state
      if (item.name && item.name.toLowerCase().indexOf(searchText) > -1) {
        return true
      }

      if (item.isMinWage && item.county && item.county.toLowerCase().indexOf(searchText) > -1) {
        return true
      }

      if (item.city && item.city.toLowerCase().indexOf(searchText) > -1) {
        return true
      }

      // state by abbreviation
      if (item.state && item.state.toLowerCase() === searchText) {
        return true
      }

      if (item.address && item.address.toLowerCase().indexOf(searchText) > -1) {
        return true
      }

      // state by name
      const state = statesByCode[item.state]
      if (state && state.name.toLowerCase().indexOf(searchText) > -1) {
        return true
      }
      return false
    }
    return true
  }
}
export const tidy = s => s.toLowerCase().trim()
// This is a list of available helper sorts that can be used to not have to rewrite any logic
export const sorts = {
  address: (a, b) => {
    if (a.country > b.country) {
      return 1
    }
    if (a.country < b.country) {
      return -1
    }

    if (a.state && b.state && statesByCode[a.state].name > statesByCode[b.state].name) {
      return 1
    }
    if (a.state && b.state && statesByCode[a.state].name < statesByCode[b.state].name) {
      return -1
    }

    if (a.city > b.city) {
      return 1
    }
    if (a.city < b.city) {
      return -1
    }

    return 0
  },
  amount: (a:any, b:any):number => {
    if (a === undefined && b === undefined) return 0
    else if (b === undefined && a !== undefined) return 1
    else if (a === undefined && b !== undefined) return -1
    else if (b === null && a !== null) return 1
    else if (a === null && b !== null) return -1
    else if (a === b) return 0
    else return (a > b) ? 1 : -1
  },
  string: (a:String, b:String):number => {
    if (!a && !b) return 0
    if (a && !b) return 1
    if (!a && b) return -1
    if (a.trim() === b.trim()) return 0
    return ([tidy(a), tidy(b)].sort()[0] === tidy(a)) ? 1 : -1
  },
  date: (a, b) => {
    if (moment(a).isSame(b)) {
      return 0
    } else if (a && !b) {
      return 1
    } else if (!a && b) {
      return -1
    } else if (moment(a).isAfter(b)) {
      return 1
    }
    return -1
  },
  stateName: (a, b) => sorts.amount(statesByCode[a].name, statesByCode[b].name)
}

export default options =>
  compose(
    withState('filterData', 'setFilterData', {}),

    withProps(props => ({
      columns: options.columns ? options.columns : props.columns,

      stateList: props.stateList,

      filter: () => {
        const returnData = []
        Object.keys(props.data).map(id => {
          let valid = true
          options.filters.map(filterFn => {
            if (filterFn(props.data[id], props.filterData, props) === false) {
              valid = false
            }
          })

          if (valid) {
            returnData.push(id)
          }
        })
        return returnData
      },

      filtered: [],

      recordCount: 0,

      showDateFilter: options.showDateFilter
    })),

    withPropsOnChange(['data', 'filterData'], props => {
      const filtered = props.filter()
      const recordCount = filtered.length
      return {
        filtered,
        recordCount
      }
    }),

    withProps(props => ({
      onFilter: filter => {
        props.setFilterData(JSON.parse(JSON.stringify(filter)))
      },

      onSort: ({
        setSortDirection,
        setSortIndex,
        setSortText,
        sortBy,
        sortDirection,
        sortIndex
      }) => {
        if (!sortBy || !sortDirection) {
          return
        }

        let column
        // Find column that matches sortBy
        props.columns.forEach(item => {
          if (item.key === sortBy) {
            column = item
          }
        })

        let sortFn
        if (column.sort) {
          if (typeof column.sort === 'function') {
            sortFn = column.sort
          } else {
            // Dealing with an array of sorts
            sortFn = column.sort[sortIndex].sort
            sortDirection = column.sort[sortIndex].sortDirection
            setSortDirection(column.sort[sortIndex].sortDirection)
            setSortText(column.sort[sortIndex].text)
            sortIndex++
            if (sortIndex >= column.sort.length) {
              sortIndex = 0
            }
            setSortIndex(sortIndex)
          }
        }

        props.filtered.sort((aIndex, bIndex) => {
          const a = props.data[aIndex]
          const b = props.data[bIndex]

          let returnData = 0

          if (sortFn) {
            returnData = sortFn(a, b)
          }

          if (sortDirection === 'DESC') {
            returnData = -returnData
          }
          return returnData
        })
      },

      renderRow: ({ index }) => {
        const item = props.data[props.filtered[index]]

        if (item) {
          return options.renderItem(item, props)
        } else {
          return null
        }
      }
    }))
  )
